/**
 * CAMADA DE ANALYTICS & MENSURAÇÃO — MEI TOOLBOX
 * 
 * Biblioteca centralizada para rastreamento de comportamento, cliques e métricas de funil
 * baseada no padrão de nomenclatura 'objeto_verbo' (ex: simulator_started).
 */

// Interface para estender o objeto window do navegador
declare global {
  interface Window {
    gtag?: (command: string, action: string, params?: Record<string, any>) => void;
    dataLayer?: any[];
  }
}

// Mapa para persistência de carimbos de tempo (timers) em memória
const timers: Record<string, number> = {};

/**
 * Recupera ou cria um identificador exclusivo de sessão temporária
 * persistido na aba ativa do navegador (sessionStorage).
 */
export function getOrCreateSessionId(): string {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return "session_ssr_mock";
  }

  let sessionId = window.sessionStorage.getItem("mei_session_id");
  if (!sessionId) {
    // Gerador simples de identificador pseudo-UUID para session_id
    sessionId = "session_" + Math.random().toString(36).substring(2, 15) + "_" + Date.now();
    window.sessionStorage.setItem("mei_session_id", sessionId);
  }
  return sessionId;
}

/**
 * Identifica a origem do tráfego através de parâmetros UTM na URL
 * ou pelo document.referrer (com fallbacks adequados).
 */
export function getTrafficSource(): string {
  if (typeof window === "undefined") {
    return "ssr";
  }

  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get("utm_source");
  if (utmSource) {
    return utmSource;
  }

  const referrer = document.referrer;
  if (referrer) {
    try {
      const url = new URL(referrer);
      if (url.hostname.includes("google.com")) return "google_organic";
      if (url.hostname.includes("instagram.com") || url.hostname.includes("t.co")) return "social";
      return url.hostname; // Retorna o domínio de referência (ex: facebook.com)
    } catch {
      return "referrer_invalid";
    }
  }

  return "direct";
}

/**
 * Determina o tipo de dispositivo (mobile, tablet, desktop) baseado
 * no tamanho horizontal do viewport do navegador.
 */
export function getDeviceType(): "desktop" | "tablet" | "mobile" {
  if (typeof window === "undefined") {
    return "desktop";
  }

  const width = window.innerWidth;
  if (width < 768) {
    return "mobile";
  }
  if (width < 1024) {
    return "tablet";
  }
  return "desktop";
}

/**
 * Retorna a variante de página ativa para experimentos A/B futuros.
 * Nesta fase inicial de baseline, retorna fixamente "control".
 */
export function getPageVariant(): string {
  return "control";
}

/**
 * Inicia um temporizador em memória para medir tempo decorrido até uma ação.
 */
export function startTimer(timerName: string): void {
  timers[timerName] = performance.now();
}

/**
 * Retorna o tempo decorrido (em segundos) desde que o temporizador foi iniciado.
 */
export function getElapsedTime(timerName: string): number {
  const startTime = timers[timerName];
  if (startTime === undefined) {
    return 0;
  }
  const durationMs = performance.now() - startTime;
  return parseFloat((durationMs / 1000).toFixed(2));
}

/**
 * Dispara um evento de tracking unificado para o Google Analytics 4 (GA4)
 * com as propriedades de contexto global injetadas automaticamente.
 */
export function trackEvent(eventName: string, params: Record<string, any> = {}): void {
  if (typeof window === "undefined") {
    return;
  }

  // Mesclar dados específicos com as variáveis globais de contexto
  const mergedParams = {
    ...params,
    session_id: getOrCreateSessionId(),
    traffic_source: getTrafficSource(),
    device_type: getDeviceType(),
    page_variant: getPageVariant(),
  };

  // Disparar para a API global do Google Analytics (se configurada)
  if (typeof window.gtag === "function") {
    window.gtag("event", eventName, mergedParams);
  }

  // Depuração local simplificada (somente em modo de desenvolvimento)
  if (import.meta.env.DEV) {
    console.info(`[Analytics Event] "${eventName}" fired:`, mergedParams);
  }
}

/**
 * Rastreia cliques no CTA do e-book na página inicial.
 * Dispara tanto um evento customizado (ebook_home_click) quanto
 * o evento recomendado generate_lead para mensuração de conversões.
 */
export function trackEbookHomeClick(ctaText: string, destination: string): void {
  const value = 29.99;
  const currency = "BRL";

  trackEvent("ebook_home_click", {
    cta_text: ctaText,
    destination,
    location: "home_ebook_section",
    value,
    currency,
  });

  trackEvent("generate_lead", {
    currency,
    value,
  });
}

/**
 * Rastreia quando o usuário efetivamente atinge o checkout externo da Kiwify.
 * Diferente de `ebook_home_click` (clique no CTA da home que leva para a LP interna),
 * este evento representa a saída rumo ao carrinho de pagamento — o passo mais
 * próximo da conversão que conseguimos observar antes do redirecionamento.
 *
 * Dispara também `begin_checkout` (evento recomendado do GA4 para funil de compra).
 */
export function trackEbookCheckoutReached(
  location: string,
  ctaText: string,
  checkoutUrl: string,
): void {
  const value = 29.99;
  const currency = "BRL";

  trackEvent("ebook_checkout_reached", {
    cta_text: ctaText,
    checkout_url: checkoutUrl,
    location,
    value,
    currency,
  });

  trackEvent("begin_checkout", {
    currency,
    value,
    items: [
      {
        item_id: "ebook-mei-2026",
        item_name: "MEI Sem Dúvidas 2026 — Manual Definitivo",
        item_brand: "MEI Toolbox",
        item_category: "ebook",
        price: value,
        quantity: 1,
      },
    ],
  });
}
